import VenueBasedTicketBasketRepository from "@app-contexts/dashboard/features/box-office/basket/data/repositories/VenueBasedTicketBasketRepository";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import BasketRepositoryFactory from "@shared/modules/basket/factory/BasketRepositoryFactory";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import { PaymentServiceBasket } from "../../services/useBasketPaymentService";
import { PurchaseOptsType } from "../../types";
import PaymentGateway from "../PaymentGateway";
import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";
import { createPaymentLogger } from "../../utils/payment-logger";

const log = createPaymentLogger("ShowpassPaymentGateway");

/**
 * ShowpassPaymentGateway handles the processing of transactions that don't involve a third-party processor.
 * For example, for Stripe and Authorize we must load a client-side SDK on the frontend in order to interface
 * with their systems directly. In those scenarios, both client-side (frontend) and server-side (Django backend)
 * interface with the payment processor to process the transaction.
 *
 * ShowpassPaymentGateway however is meant for scenarios that processes transactions purely in-house, for example:
 * - Free baskets -- regardless of whether it is after discounts, or a $0 item
 * - Waitlists without cc_verification_required
 */
class ShowpassPaymentGateway extends PaymentGateway {
  public static gatewayName = "ShowpassPaymentGateway";

  async initialize() {
    log.info("Initializing");
    return this;
  }

  // eslint-disable-next-line class-methods-use-this
  async confirmPayment(
    _: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!basket.basketId) {
      throw new PaymentProcessingError(
        "Cannot perform a purchase call with an invalid basket id"
      );
    }

    const repository:
      | UserBasedTicketBasketRepository
      | VenueBasedTicketBasketRepository
      | undefined = BasketRepositoryFactory.getRepository(
      basketType,
      basket.paymentVenue
    );

    if (!repository) {
      throw new PaymentConfigurationError(
        "Invalid repository selected to perform purchase call"
      );
    }

    return repository.purchase(
      basket.basketId,
      {
        ...purchaseOpts,
        payment_type: basket.isFree
          ? // Free payment types must be manually set by us and may not always be automatically updated
            // when we change payment types or when the total becomes free. Hence the determination here.
            PAYMENT_TYPE_OPTIONS_MAP.FREE.id
          : basket.paymentType,
        purchase_source_platform: basket.purchaseSourcePlatform,
        uuid: basket.basketUuid,
      },
      {
        showToastOnError: false, // Should be handled by PaymentManager
      }
    );
  }

  /**
   * Update payment object for a free order.
   * For free orders, we simply return the basket as is since no payment processing is needed.
   */
  async updatePaymentObject(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    basket: Pick<PaymentServiceBasket, "basketId" | "currency" | "totalPrice">
  ) {
    // No payment object updates needed for free orders

    log.info("Skipping updatePaymentObject — no lifecycle objects");
    return this;
  }

  /**
   * Post-process the checkout for a free order.
   * For free orders, we simply return the basket and invoice information.
   */
  async postProcessCheckout() {
    log.info("Checking for invoice");

    /* eslint-disable no-await-in-loop -- https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it */
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    if (!this.basketPaymentService || !this.invoicePaymentService) {
      throw new PaymentInitializationError(
        "Tried to post process a checkout without required payment services",
        {
          metadata: {
            hasBasketService: !!this.basketPaymentService,
            hasInvoiceService: !!this.invoicePaymentService,
          },
        }
      );
    }

    // First, poll for the basket until we get a valid invoice_id
    const MAX_BASKET_ATTEMPTS = 5;
    let finalBasket;
    let invoiceId = null;

    for (let attempts = 1; attempts <= MAX_BASKET_ATTEMPTS; attempts++) {
      finalBasket = await this.basketPaymentService.refresh({
        no_lock: true,
      });

      if (finalBasket?.id == null) {
        throw new PaymentProcessingError(
          "Could not obtain a basket id for post purchase",
          {
            metadata: { attempts },
            userMessage:
              "We're having trouble completing your order. Please try again.",
          }
        );
      }

      // If we have an invoice ID, break out of the polling loop
      if (finalBasket.invoice_id != null) {
        invoiceId = finalBasket.invoice_id;
        break;
      }

      // Wait before trying again if we haven't found an invoice_id yet
      if (attempts < MAX_BASKET_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // If we couldn't get an invoice_id after all attempts, throw an error
    if (invoiceId == null) {
      throw new PaymentProcessingError(
        "Could not obtain an invoice ID for this purchase",
        {
          metadata: { attempts: MAX_BASKET_ATTEMPTS },
          userMessage:
            "Your payment was processed but we're having trouble generating your receipt. Please contact support.",
        }
      );
    }

    if (finalBasket == null) {
      throw new PaymentProcessingError(
        "Could not obtain a basket for post purchase",
        {
          metadata: { finalBasket },
          userMessage:
            "We're having trouble completing your order. Please try again.",
        }
      );
    }

    return finalBasket;
  }
}

export default ShowpassPaymentGateway;
